import { UseFormSetValue } from 'react-hook-form';
import { DecomPolicyFormValues } from '../../decom-policy-form-layout.tsx';
import { VaultMatterModel } from '../../../../../../api/vault-api.ts';
import { useMemo } from 'react';
import SharedDataGridClient from '../../../../../../shared/shared-data-grid-client.tsx';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { clearFieldError } from '../decom-action.tsx';
import { ActionErrorEntry } from '../../decom-policy-utils.tsx';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import CreateNewFolderOutlined from '@mui/icons-material/CreateNewFolderOutlined';
import Search from '@mui/icons-material/Search';

interface VaultMatterTableProps {
  matters: VaultMatterModel[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  matterId: string;
  day: number;
  idx: number;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  setIsCreateMatterOpen: (open: boolean) => void;
  hasUnfilteredData: boolean;
  hasError?: boolean;
  actionType: string;
  setActionTypesWithErrors: React.Dispatch<
    React.SetStateAction<ActionErrorEntry[]>
  >;
  disabled?: boolean;
}

export default function VaultMatterTableOld({
  matters,
  isLoading,
  isError,
  refetch,
  matterId,
  day,
  idx,
  setValue,
  setIsCreateMatterOpen,
  hasUnfilteredData,
  hasError,
  actionType,
  setActionTypesWithErrors,
  disabled,
}: VaultMatterTableProps) {
  const theme = useTheme();

  const columns: GridColDef<VaultMatterModel>[] = useMemo(
    () => [
      {
        field: 'select',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        renderCell: (params: GridRenderCellParams<VaultMatterModel>) => (
          <Radio
            checked={matterId === params.row.matterId}
            disabled={disabled}
            onChange={() => {
              setValue(
                `actions.${day}.actions.${idx}.matterId`,
                params.row.matterId,
                {
                  shouldDirty: true,
                  shouldValidate: true,
                }
              );
              clearFieldError(actionType, 'matterId', setActionTypesWithErrors);
            }}
            value={params.row.matterId}
            sx={{
              p: 0.5,
            }}
          />
        ),
      },
      {
        field: 'name',
        headerName: 'Matter Name',
        flex: 1,
        sortable: true,
        resizable: false,
        disableColumnMenu: true,
        valueGetter: (_value, row) => row.name,
      },
    ],
    [
      matterId,
      day,
      idx,
      setValue,
      actionType,
      setActionTypesWithErrors,
      disabled,
    ]
  );

  if (isError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Stack alignItems="center" spacing={1}>
          <Typography color="error">
            Failed to load matters. Please try again.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!isLoading && matters.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Box
          sx={{
            bgcolor: 'info.light',
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 1,
            p: 6,
            textAlign: 'center',
            mt: 2,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.paper',
              borderRadius: '50%',
              width: 64,
              height: 64,
              mb: 2,
            }}
          >
            {!hasUnfilteredData ? (
              <CreateNewFolderOutlined color="primary" sx={{ fontSize: 28 }} />
            ) : (
              <Search color="primary" sx={{ fontSize: 28 }} />
            )}
          </Box>
          <Typography variant="body1" color="text.primary" mb={2}>
            {!hasUnfilteredData
              ? "There aren't any matters created in this organization."
              : 'No matters found matching the current filters.'}
          </Typography>
          {!hasUnfilteredData && (
            <Button
              variant="contained"
              onClick={() => setIsCreateMatterOpen(true)}
            >
              Create Matter
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: 350,
        border: hasError ? '1px solid' : undefined,
        borderColor: hasError ? theme.palette.error.main : undefined,
        borderRadius: hasError ? 1 : 0,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <SharedDataGridClient
        rows={matters}
        columns={columns}
        getRowId={(row) => row.matterId}
        loading={isLoading}
        showToolbar={false}
        pagination={true}
        pageSizeOptions={[25, 50, 100]}
        defaultGridState={{
          pagination: {
            paginationModel: {
              pageSize: 25,
              page: 0,
            },
          },
        }}
        rowHeight={52}
        hideFooterRowCount={true}
        sx={{
          height: '100%',
          boxShadow: 'none',
          border: 'none',
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
          '& .MuiDataGrid-cell': {
            outline: 'none !important',
            borderRight: 'none !important',
          },
          '& .MuiDataGrid-cellFocus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-columnHeaderFocus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-columnHeader:focus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: 'none !important',
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none !important',
          },
        }}
      />
    </Box>
  );
}
